import React, { useMemo } from 'react';
import type { CoopCrosswordBoard, CoopRuntime } from '../../core/types/payloads/coop.types';
import { buildCrosswordGrid, cellKey, type CrosswordGridMeta } from './crossword.engine';

const INK = '#1c1917';
const BLACK = '#111111';
const WHITE = '#ffffff';
const HIGHLIGHT = '#fff3b0';
const HIGHLIGHT_WORD = '#dbeafe';
const BORDER = '#2a2a2a';

function CoopShell({
  title,
  badge,
  children,
  footer,
}: {
  title: string;
  badge?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
}) {
  return (
    <div
      className="relative overflow-hidden rounded-2xl p-5 sm:p-8"
      style={{
        background: 'var(--plugin-bg)',
        border: '1px solid var(--plugin-border)',
        color: 'var(--plugin-text)',
      }}
    >
      <div className="relative">
        {badge && (
          <span
            className="mb-3 inline-block rounded-full px-3 py-1 text-[10px] font-bold uppercase tracking-widest"
            style={{ background: 'var(--plugin-btn-bg)', color: 'var(--sala-accent-neon, #6366f1)' }}
          >
            {badge}
          </span>
        )}
        <h3 className="font-display text-xl font-bold sm:text-2xl">{title}</h3>
        {children}
        {footer}
      </div>
    </div>
  );
}

function CrosswordGrid({
  board,
  meta,
  runtime,
  size = 'md',
  onCellClick,
  readOnly,
}: {
  board: CoopCrosswordBoard;
  meta: CrosswordGridMeta;
  runtime: CoopRuntime;
  size?: 'md' | 'lg';
  onCellClick?: (key: string) => void;
  readOnly?: boolean;
}) {
  const cellSize = size === 'lg' ? 'min(48px, 7.5vw)' : 'min(36px, 10vw)';
  const activeClueId = runtime.activeClueId;
  const activeCells = activeClueId ? new Set(meta.clueCells[activeClueId] ?? []) : new Set<string>();

  return (
    <div
      className="crossword-grid inline-grid"
      style={{
        gridTemplateColumns: `repeat(${board.cols}, ${cellSize})`,
        border: `2px solid ${INK}`,
        background: BLACK,
        boxShadow: '0 4px 24px rgba(0,0,0,0.08)',
      }}
    >
      {Array.from({ length: board.rows }, (_, row) =>
        Array.from({ length: board.cols }, (_, col) => {
          const key = cellKey(row, col);
          const blocked = meta.blocked.has(key);
          const filled = runtime.filledCells[key];
          const selected = runtime.selectedCell === key;
          const inWord = activeCells.has(key);
          const number = meta.cellNumbers[key];

          if (blocked) {
            return (
              <div
                key={key}
                className="crossword-cell crossword-cell--black aspect-square"
                style={{ background: BLACK }}
              />
            );
          }

          return (
            <button
              key={key}
              type="button"
              disabled={readOnly && !onCellClick}
              onClick={() => onCellClick?.(key)}
              className="crossword-cell crossword-cell--white relative flex aspect-square items-center justify-center transition-colors duration-150"
              style={{
                fontSize: size === 'lg' ? '1.25rem' : '1rem',
                fontFamily: 'Georgia, "Times New Roman", serif',
                fontWeight: 600,
                letterSpacing: '0.04em',
                background: selected ? HIGHLIGHT : inWord ? HIGHLIGHT_WORD : WHITE,
                color: INK,
                border: `1px solid ${BORDER}`,
                cursor: onCellClick ? 'pointer' : 'default',
              }}
            >
              {number && (
                <span
                  className="absolute left-[3px] top-[2px] font-sans text-[7px] font-bold leading-none sm:text-[8px]"
                  style={{ color: INK }}
                >
                  {number}
                </span>
              )}
              {(filled ?? '').toUpperCase()}
            </button>
          );
        }),
      )}
    </div>
  );
}

function ClueList({
  board,
  meta,
  runtime,
  direction,
  onSelectClue,
}: {
  board: CoopCrosswordBoard;
  meta: CrosswordGridMeta;
  runtime: CoopRuntime;
  direction: 'across' | 'down';
  onSelectClue?: (clueId: string) => void;
}) {
  const clues = board.clues.filter((c) => c.direction === direction);
  const label = direction === 'across' ? 'Horizontales' : 'Verticales';

  return (
    <div>
      <p className="mb-2 text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--plugin-muted)' }}>
        {label}
      </p>
      <ul className="space-y-1.5">
        {clues.map((clue) => {
          const done = runtime.completedIds.includes(clue.id);
          const active = runtime.activeClueId === clue.id;
          const num = meta.clueNumbers[clue.id];
          return (
            <li key={clue.id}>
              <button
                type="button"
                disabled={!onSelectClue}
                onClick={() => onSelectClue?.(clue.id)}
                className="w-full rounded-lg px-3 py-2 text-left text-sm transition"
                style={{
                  background: active ? HIGHLIGHT_WORD : 'var(--plugin-btn-bg)',
                  border: active ? `1px solid ${INK}` : '1px solid var(--plugin-border)',
                  opacity: done ? 0.55 : 1,
                  textDecoration: done ? 'line-through' : undefined,
                }}
              >
                <span className="mr-2 font-bold tabular-nums">{num}.</span>
                {clue.clue}
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}

const LETTERS = 'ABCDEFGHIJKLMNÑOPQRSTUVWXYZ'.split('');

function LetterKeyboard({
  onLetter,
  disabled,
}: {
  onLetter: (letter: string) => void;
  disabled?: boolean;
}) {
  return (
    <div className="mt-4 grid grid-cols-7 gap-1.5 sm:grid-cols-9">
      {LETTERS.map((letter) => (
        <button
          key={letter}
          type="button"
          disabled={disabled}
          className="rounded-md border py-2.5 font-sans text-sm font-semibold transition active:scale-95 disabled:opacity-40 sm:text-base"
          style={{
            background: WHITE,
            borderColor: BORDER,
            color: INK,
          }}
          onClick={() => onLetter(letter)}
        >
          {letter}
        </button>
      ))}
    </div>
  );
}

export function CrosswordStage({
  board,
  runtime,
  title,
  badge,
  size = 'md',
  canAct,
  turnLabel,
  dispatch,
  readOnly,
}: {
  board: CoopCrosswordBoard;
  runtime: CoopRuntime;
  title: string;
  badge?: string;
  size?: 'md' | 'lg';
  canAct?: boolean;
  turnLabel?: string;
  dispatch?: (action: { type: string; payload?: unknown }) => void;
  readOnly?: boolean;
}) {
  const meta = useMemo(() => buildCrosswordGrid(board), [board]);

  const handleCellClick = (key: string) => {
    if (!dispatch) return;
    dispatch({ type: 'SELECT_CELL', payload: { cellKey: key } });
  };

  const handleClueClick = (clueId: string) => {
    dispatch?.({ type: 'SELECT_CLUE', payload: { clueId } });
  };

  const handleLetter = (letter: string) => {
    if (!dispatch || !canAct) return;
    const cell = runtime.selectedCell;
    if (!cell) {
      const firstClue = board.clues.find((c) => !runtime.completedIds.includes(c.id));
      const start = firstClue ? meta.clueCells[firstClue.id]?.[0] : null;
      if (start) dispatch({ type: 'SELECT_CELL', payload: { cellKey: start } });
      return;
    }
    dispatch({ type: 'FILL_CELL', payload: { cellKey: cell, letter } });
  };

  const progress = board.clues.length
    ? Math.round((runtime.completedIds.length / board.clues.length) * 100)
    : 0;

  return (
    <CoopShell
      title={title}
      badge={badge}
      footer={
        <div className="mt-6 flex flex-wrap items-center justify-between gap-3 border-t pt-4" style={{ borderColor: 'var(--plugin-border)' }}>
          <p className="text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--plugin-muted)' }}>
            {runtime.completedIds.length}/{board.clues.length} pistas · {progress}%
          </p>
          {turnLabel && (
            <p className="text-sm font-semibold" style={{ color: 'var(--plugin-text)' }}>
              {turnLabel}
            </p>
          )}
          {runtime.won && (
            <p className="font-display text-lg font-bold text-emerald-600">¡Crucigrama completado!</p>
          )}
        </div>
      }
    >
      <div className={`mt-6 grid gap-6 ${size === 'lg' ? 'lg:grid-cols-[1fr_280px]' : ''}`}>
        <div className="flex justify-center overflow-x-auto pb-2">
          <CrosswordGrid
            board={board}
            meta={meta}
            runtime={runtime}
            size={size}
            onCellClick={dispatch && !readOnly ? handleCellClick : undefined}
            readOnly={readOnly}
          />
        </div>

        <div className="space-y-5">
          <ClueList board={board} meta={meta} runtime={runtime} direction="across" onSelectClue={dispatch ? handleClueClick : undefined} />
          <ClueList board={board} meta={meta} runtime={runtime} direction="down" onSelectClue={dispatch ? handleClueClick : undefined} />
        </div>
      </div>

      {canAct && dispatch && !runtime.won && (
        <div>
          <p className="mt-6 text-center text-xs font-bold uppercase tracking-widest" style={{ color: 'var(--plugin-muted)' }}>
            {runtime.selectedCell
              ? `Celda ${runtime.selectedCell.replace(':', '·')}`
              : 'Selecciona una celda o pista'}
          </p>
          <LetterKeyboard onLetter={handleLetter} />
        </div>
      )}

      {!canAct && !readOnly && !runtime.won && (
        <p className="mt-6 rounded-xl px-4 py-5 text-center text-sm" style={{ background: 'var(--plugin-btn-bg)', color: 'var(--plugin-muted)' }}>
          Espera tu turno para escribir…
        </p>
      )}
    </CoopShell>
  );
}

export function getTurnLabel(runtime: CoopRuntime, turnUnit: 'participant' | 'team' = 'team'): string {
  if (!runtime.currentTurn) return 'Sin turno asignado';
  if (turnUnit === 'team') return `Turno: equipo ${runtime.currentTurn.teamId ?? '—'}`;
  return `Turno: ${runtime.currentTurn.participantId ?? '—'}`;
}
